import { useEffect, useState, useCallback } from "react"
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Paper,
  TableContainer,
  TablePagination,
  Chip,
  CircularProgress,
  styled,
  keyframes,
  IconButton,
  Grid,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import api from "../services/api"
import { API_ENDPOINTS } from "../config/api"
// import { useAuth } from "../context/AuthContext"
import { useNavigate } from "react-router-dom"
import { DesktopWindowsOutlined, MapOutlined } from "@mui/icons-material"

const loadingAnimation = keyframes`
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
`

const LoadingBar = styled("div")(({ theme }) => ({
  height: "2px",
  width: "100%",
  position: "relative",
  overflow: "hidden",
  backgroundColor: "transparent",
  "&::before": {
    content: '""',
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: theme.palette.primary.main,
    animation: `${loadingAnimation} 1.5s infinite`,
  },
}))

const Dashboard = () => {
  const [userActivities, setUserActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [totalItems, setTotalItems] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  // const { user } = useAuth()
  const navigate = useNavigate()

  const fetchUserActivities = useCallback(async (isRefreshing = false) => {
    try {
      if (!isRefreshing) {
        setLoading(true)
      }
      setRefreshing(true)
      setError(null)
      const response = await api.get(API_ENDPOINTS.USER_ACTIVITY.GET_ALL, {
        params: { timestamp: new Date().getTime() },
      })
      const { userActivities, totalItems } = response.data
      setUserActivities(userActivities || [])
      setTotalItems(totalItems || 0)
    } catch (err) {
      console.error("Error fetching user activities:", err)
      setError("Failed to fetch user activity logs")
      setUserActivities([])
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchUserActivities(false)
  }, [fetchUserActivities])

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleRefresh = () => {
    fetchUserActivities(true)
  }

  const displayedActivities = userActivities.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Box sx={{ paddingRight: 2, paddingLeft: 1, marginBottom: "100px" }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", mb: 4 }}>
        Dashboard
      </Typography>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ marginY: 4, paddingLeft: 1 }}>
        <Grid item xs={12} sm={6} md={6} sx={{ paddingX: 2 }}>
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": { boxShadow: 6 },
              transition: "box-shadow 0.3s ease-in-out",
            }}
            onClick={() => navigate("/cctv")}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "500", color: "text.primary" }}>
                CCTV Monitoring
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <DesktopWindowsOutlined sx={{ fontSize: 48, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={6} sx={{ paddingX: 2 }}>
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": { boxShadow: 6 },
              transition: "box-shadow 0.3s ease-in-out",
            }}
            onClick={() => navigate("/ais-monitoring")}
          >
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: "500", color: "text.primary" }}>
                AIS Monitoring
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <MapOutlined sx={{ fontSize: 48, color: "primary.main" }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User Activity Logs Table */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: "600", marginBottom: 2 }}>
          Latest User Activity
        </Typography>
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            borderRadius: 3,
            boxShadow: 2,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table stickyHeader aria-label="user activity table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>User</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Details</TableCell>
                      <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>Date</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={handleRefresh} disabled={refreshing} sx={{ color: "text.secondary" }}>
                          <RefreshIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refreshing && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ padding: 0 }}>
                          <LoadingBar />
                        </TableCell>
                      </TableRow>
                    )}
                    {error ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", color: "error.main" }}>
                          {error}
                        </TableCell>
                      </TableRow>
                    ) : displayedActivities.length > 0 ? (
                      displayedActivities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>{activity.user_id.name}</TableCell>
                          <TableCell>{activity.client_id.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={activity.action.toUpperCase()}
                              size="small"
                              sx={{
                                bgcolor: "primary.main",
                                color: "white",
                                fontWeight: "bold",
                                borderRadius: 1,
                                px: 2,
                                py: 1.5,
                              }}
                            />
                          </TableCell>
                          <TableCell>{activity.details}</TableCell>
                          <TableCell>
                            {new Date(activity.created_at).toLocaleString("id-ID", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: true,
                            })}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", color: "text.primary" }}>
                          No user activity data available.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 15]}
                component="div"
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  )
}

export default Dashboard
